import { useState } from "react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";

const defaultInterests = [
  { value: "kitchen", label: "Modular Kitchen" },
  { value: "wardrobe", label: "Wardrobe" },
  { value: "office", label: "Office Furniture" },
  { value: "residential", label: "Residential Storage" },
  { value: "general", label: "General Enquiry" },
];

export const EnquiryForm = ({
  variant = "contact", // "contact" | "dealer"
  defaultInterest = "",
  title,
  subtitle,
}) => {
  const isDealer = variant === "dealer";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    interest: defaultInterest,
    message: "",
    current_business: "",
    investment_capacity: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({
        type: isDealer ? "dealer" : "contact",
        ...form,
      });
      toast.success(
        isDealer
          ? "Application received. Our partnerships team will call you within 2 working days."
          : "Thank you — we've received your enquiry. Our team will be in touch shortly."
      );
      setForm({
        name: "", email: "", phone: "", city: "",
        interest: defaultInterest, message: "",
        current_business: "", investment_capacity: "",
      });
    } catch (err) {
      toast.error("Could not submit right now. Please call us or try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-b border-brandgray bg-transparent py-3 px-0 focus:outline-none focus:border-navy transition-colors text-black placeholder:text-black/40 rounded-none";

  return (
    <form
      onSubmit={onSubmit}
      data-testid={isDealer ? "dealer-form" : "contact-form"}
      className="bg-white border border-brandgray p-6 md:p-10 shadow-[0_8px_30px_rgba(41,72,144,0.06)]"
    >
      {title && <h3 className="font-display text-2xl md:text-3xl tracking-tight text-navy">{title}</h3>}
      {subtitle && <p className="mt-2 mb-8 text-sm md:text-base text-black/70">{subtitle}</p>}
      {!subtitle && title && <div className="mb-8" />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <div>
          <label className="overline block mb-2">Full Name</label>
          <input data-testid="form-name" className={inputCls} value={form.name} onChange={update("name")} placeholder="Your name" />
        </div>
        <div>
          <label className="overline block mb-2">Phone</label>
          <input data-testid="form-phone" className={inputCls} value={form.phone} onChange={update("phone")} placeholder="+91" />
        </div>
        <div>
          <label className="overline block mb-2">Email</label>
          <input data-testid="form-email" type="email" className={inputCls} value={form.email} onChange={update("email")} placeholder="you@example.com" />
        </div>
        <div>
          <label className="overline block mb-2">City</label>
          <input data-testid="form-city" className={inputCls} value={form.city} onChange={update("city")} placeholder="Your city" />
        </div>

        {!isDealer && (
          <div className="md:col-span-2">
            <label className="overline block mb-2">Interested In</label>
            <select data-testid="form-interest" className={`${inputCls} appearance-none`} value={form.interest} onChange={update("interest")}>
              <option value="">Select a category</option>
              {defaultInterests.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </select>
          </div>
        )}

        {isDealer && (
          <>
            <div>
              <label className="overline block mb-2">Current Business</label>
              <input data-testid="form-current-business" className={inputCls} value={form.current_business} onChange={update("current_business")} placeholder="Existing showroom, retail, contracting…" />
            </div>
            <div>
              <label className="overline block mb-2">Investment Capacity</label>
              <select data-testid="form-investment" className={`${inputCls} appearance-none`} value={form.investment_capacity} onChange={update("investment_capacity")}>
                <option value="">Select a range</option>
                <option value="5-10L">₹5 – 10 Lakh</option>
                <option value="10-25L">₹10 – 25 Lakh</option>
                <option value="25-50L">₹25 – 50 Lakh</option>
                <option value="50L+">₹50 Lakh+</option>
              </select>
            </div>
          </>
        )}

        <div className="md:col-span-2">
          <label className="overline block mb-2">{isDealer ? "Tell us about your market" : "Message"}</label>
          <textarea data-testid="form-message" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={update("message")} placeholder={isDealer ? "Territory, timeline, questions…" : "Project details, timeline, questions…"} />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="form-submit"
        className="mt-10 inline-flex items-center gap-3 bg-coral hover:bg-[#d66a54] disabled:opacity-60 text-white px-8 py-3.5 tracking-wide transition-colors group"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />}
        <span>{loading ? "Submitting…" : isDealer ? "Apply for Dealership" : "Send Enquiry"}</span>
      </button>
    </form>
  );
};

export default EnquiryForm;
